import { useBreadcrumbs } from "#routers/breadcrumbsHelper";
import { useNavigate } from "#routers/hooks";
import { AccessDeniedLayout } from "#src/batteries-included-components/Layouts/Authorization/AccessDenied";
import {
  CreateUserDetailsStep,
  defaultHumanValues,
  UserCreateFormType,
} from "#src/batteries-included-components/Panels/FormPanels/CreateUserFormPanel";
import { useIsOpsHubExperience } from "#src/contexts/AuthenticatedContext.helpers";
import { SETTINGS_BREADCRUMB } from "#src/routes/settings";
import {
  linkToUsersListPage,
  USERS_LIST_BREADCRUMB,
} from "#src/routes/settings/users";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Form,
  Page,
  useForm,
  useToast,
} from "@validereinc/common-components";
import {
  CreateHumanUserInputSchema,
  CreateMachineUserInputSchema,
  RolesAdapter,
  UsersAdapter,
  UserStatus,
  UserTypeEnum,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React from "react";
import { CREATE_USER_BREADCRUMB, CREATE_USER_PAGE_TITLE } from ".";
import styles from "./CreateUserPage.module.scss";

const cx = classNames.bind(styles);

export const CreateUserPageContent = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const form = useForm<UserCreateFormType>({
    defaultValues: defaultHumanValues,
  });

  const queryClient = useQueryClient();
  const breadcrumbs = useBreadcrumbs([
    SETTINGS_BREADCRUMB,
    USERS_LIST_BREADCRUMB,
    CREATE_USER_BREADCRUMB,
  ]);

  const onCancelClick = () => {
    navigate({
      pathname: linkToUsersListPage(),
    });
  };

  const createUserMutation = useMutation({
    mutationFn: async (values: UserCreateFormType) => {
      const createBody =
        values.type === UserTypeEnum.human
          ? {
              name: values.name,
              type: UserTypeEnum.human,
              status: UserStatus.active,
              email: values.email,
              phone: values.phone ? values.phone : null,
              timezone: values.timezone,
              quicksight: values.quicksight ?? false,
              can_access_quicksight_dashboards: values.quicksight ?? false,
              can_access_quicksight_console:
                values.can_access_quicksight_console ?? false,
            }
          : {
              name: values.name,
              type: UserTypeEnum.machine,
              status: UserStatus.active,
              timezone: values.timezone,
            };
      const parseResult =
        values.type === UserTypeEnum.human
          ? CreateHumanUserInputSchema.safeParse(createBody)
          : CreateMachineUserInputSchema.safeParse(createBody);

      if (!parseResult.success) {
        throw new Error("The form was not filled out correctly.");
      }

      const createdUser = await UsersAdapter.createOne({
        body: parseResult.data,
      });

      if (values.roles?.length) {
        if (!createdUser.data.id) {
          throw new Error(
            "Created user details not available to assign roles."
          );
        }
        await Promise.all(
          values.roles.map((roleId) =>
            RolesAdapter.members.update({
              roleId,
              userId: createdUser.data.id,
            })
          )
        );
      }

      return createdUser;
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to create user.",
      });
    },
    onSuccess: (createdUser) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.push({
        intent: "success",
        description: `Successfully created user: ${createdUser.data.name}.`,
      });
      navigate({
        pathname: linkToUsersListPage(),
      });
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    return createUserMutation.mutateAsync(values);
  });

  const isLoading = createUserMutation.isLoading;

  return (
    <Page
      breadcrumbs={breadcrumbs}
      title={CREATE_USER_PAGE_TITLE}
      footer={
        <div className={cx("footerContainer")}>
          <Button
            disabled={isLoading}
            onClick={onCancelClick}
          >
            Cancel
          </Button>

          <div className={cx("stepperFooterContainer")}>
            <Button
              variant="primary"
              onClick={() => void onSubmit()}
              disabled={!form.formState.isValid}
              isLoading={isLoading}
            >
              Create
            </Button>
          </div>
        </div>
      }
    >
      <Form {...form}>
        <CreateUserDetailsStep />
      </Form>
    </Page>
  );
};

export const CreateUserPage = () => {
  const [isOpsExperienceEnabled] = useIsOpsHubExperience();

  if (isOpsExperienceEnabled) {
    return <AccessDeniedLayout />;
  }

  return <CreateUserPageContent />;
};
